interface HL7Segment {
  segmentId: string;
  fields: string[];
}

interface TestResult {
  testName: string;
  value: string;
  unit: string;
  referenceRange: string;
  status: string;
  notes: string[];
}

interface MemberInfo {
  id: string;
  name: string;
  dateOfBirth: string;
  gender: string;
  dateSampleReceived?: string;
}

interface ParsedHL7 {
  segments: HL7Segment[];
  messageType: string;
  messageControlId: string;
  timestamp: string;
  testResults: TestResult[];
  memberInfo: MemberInfo;
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  // Handle YYYYMMDD format
  if (dateStr.length >= 8) {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${year}-${month}-${day}`;
  }
  return dateStr;
};

const extractNameFromPID = (pidSegment: HL7Segment): string => {
  const nameField = pidSegment.fields[4] || '';
  if (!nameField) return '';
  
  const nameParts = nameField.split('^');
  // Format: LastName^FirstName^MiddleName^Suffix^Prefix
  if (nameParts.length > 1) {
    const firstName = nameParts[1] || '';
    const lastName = nameParts[0] || '';
    // Filter out empty parts and suffixes (like 'L')
    const cleanFirstName = firstName.replace(/[^a-zA-Z\s]/g, '').trim();
    const cleanLastName = lastName.replace(/[^a-zA-Z\s]/g, '').trim();
    return `${cleanFirstName} ${cleanLastName}`.trim();
  }
  return nameParts[0] || '';
};

const extractNameFromPV = (segments: HL7Segment[]): string => {
  // Find PV* segments (PV1, PV2, etc.)
  const pvSegment = segments.find((s) => s.segmentId.match(/^PV\d+$/));
  if (!pvSegment) return '';
  
  // PV segments typically have patient name in field 4
  const nameField = pvSegment.fields[3] || '';
  if (!nameField) return '';
  
  const nameParts = nameField.split('^');
  if (nameParts.length > 1) {
    return `${nameParts[1]} ${nameParts[0]}`.trim(); // FirstName LastName
  }
  return nameParts[0] || '';
};

const extractNameFromOBR = (segments: HL7Segment[]): string => {
  const obrSegment = segments.find((s) => s.segmentId === 'OBR');
  if (!obrSegment) return '';
  
  const obrNameRaw = obrSegment.fields[30] || ''; // Field 31 (0-indexed is 30)
  if (!obrNameRaw) return '';
  
  const obrNameParts = obrNameRaw.split('&');
  return obrNameParts.length === 2 ? obrNameParts[1].trim() : obrNameRaw.trim();
};

const extractDateOfBirthFromPV = (segments: HL7Segment[]): string => {
  // Find PV* segments (PV1, PV2, etc.)
  const pvSegment = segments.find((s) => s.segmentId.match(/^PV\d+$/));
  if (!pvSegment) return '';
  
  // PV segments typically have date of birth in field 7 (0-indexed is 6)
  // But let's also check if there's a date in the last field
  const dobField = pvSegment.fields[6] || '';
  const lastField = pvSegment.fields[pvSegment.fields.length - 1] || '';
  
  // Try the standard field first, then the last field
  const dobFromStandard = formatDate(dobField);
  if (dobFromStandard) return dobFromStandard;
  
  return formatDate(lastField);
};

const extractDateOfBirthFromOBX = (segments: HL7Segment[]): string => {
  // Look for OBX segments that might contain date of birth information
  const obxSegments = segments.filter((s) => s.segmentId === 'OBX');
  
  for (const obxSegment of obxSegments) {
    const testField = obxSegment.fields[2] || '';
    const testParts = testField.split('^');
    const testName = testParts.length > 1 ? testParts[1] : testParts[0];
    
    // Check if this OBX segment contains date of birth information
    if (testName.toLowerCase().includes('birth') || 
        testName.toLowerCase().includes('dob') ||
        testName.toLowerCase().includes('date of birth')) {
      const value = obxSegment.fields[4] || '';
      return formatDate(value);
    }
  }
  
  return '';
};

const extractMemberDateOfBirth = (segments: HL7Segment[]): string => {
  // Priority order: PID → PV* → OBX
  const pidSegment = segments.find((s) => s.segmentId === 'PID');
  
  if (pidSegment) {
    const dobFromPID = formatDate(pidSegment.fields[6] || '');
    if (dobFromPID) return dobFromPID;
  }
  
  const dobFromPV = extractDateOfBirthFromPV(segments);
  if (dobFromPV) return dobFromPV;
  
  const dobFromOBX = extractDateOfBirthFromOBX(segments);
  return dobFromOBX;
};

const extractMemberName = (segments: HL7Segment[]): string => {
  // Priority order: PID → PV* → OBR
  const pidSegment = segments.find((s) => s.segmentId === 'PID');
  
  if (pidSegment) {
    const nameFromPID = extractNameFromPID(pidSegment);
    if (nameFromPID) return nameFromPID;
  }
  
  const nameFromPV = extractNameFromPV(segments);
  if (nameFromPV) return nameFromPV;
  
  return extractNameFromOBR(segments);
};

export const parseHL7 = (content: string): ParsedHL7 => {
  try {
    // First, normalize line endings and split into lines
    // Handle both CRLF and CR line terminators
    const lines = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

    // Parse each line into segments
    const segments: HL7Segment[] = [];

    lines.forEach((line) => {
      if (line.trim()) {
        // Split the line into segments by looking for segment IDs (3 characters followed by |)
        const segmentMatches = line.match(/[A-Z]{3}\|[^|]*/g) || [];

        segmentMatches.forEach((segmentStr) => {
          // Find the next segment ID or end of string
          const nextSegmentIndex = line.indexOf(segmentStr) + segmentStr.length;
          const nextSegmentMatch = line
            .slice(nextSegmentIndex)
            .match(/^[A-Z]{3}\|/);
          const endIndex = nextSegmentMatch
            ? nextSegmentIndex +
            line.slice(nextSegmentIndex).indexOf(nextSegmentMatch[0])
            : line.length;

          // Get the complete segment including all fields
          const completeSegment = line.slice(
            line.indexOf(segmentStr),
            endIndex
          );
          const fields = completeSegment.split('|');

          if (fields.length > 0) {
            const segment = {
              segmentId: fields[0],
              fields: fields.slice(1)
            };
            segments.push(segment);
          }
        });
      }
    });

    if (segments.length === 0) {
      throw new Error('No valid HL7 segments found in content');
    }

    // Extract common HL7 fields
    const mshSegment = segments.find((s) => s.segmentId === 'MSH');
    if (!mshSegment) {
      throw new Error('MSH segment not found in HL7 message');
    }

    const messageType = mshSegment.fields[8] || '';
    const messageControlId = mshSegment.fields[9] || '';
    const timestamp = mshSegment.fields[5] || '';

    // Extract member information from PID segment
    const pidSegment = segments.find((s) => s.segmentId === 'PID');
    if (!pidSegment) {
      throw new Error('PID segment not found in HL7 message');
    }

    const memberId = pidSegment.fields[2] || '';
    const name = extractMemberName(segments);

    const dateOfBirth = extractMemberDateOfBirth(segments);
    const gender = pidSegment.fields[7] || '';

    const obrSegment = segments.find((s) => s.segmentId === 'OBR');
    const dateSampleReceived = obrSegment ? formatDate(obrSegment.fields[5] || '') : '';

    const memberInfo: MemberInfo = {
      id: memberId,
      name,
      dateOfBirth,
      gender,
      dateSampleReceived
    };

    // Parse test results
    const testResults: TestResult[] = [];
    let currentTestIndex = -1;

    segments.forEach((segment) => {
      if (segment.segmentId === 'OBX') {
        // Extract test information from OBX segment
        const testField = segment.fields[2] || '';
        const testParts = testField.split('^');

        // Get the test name - prefer the second part (description) if available, otherwise use the first part (code)
        const testName = testParts.length > 1 ? testParts[1] : testParts[0];

        const value = segment.fields[4] || '';
        const unit = segment.fields[5] || '';
        const referenceRange = segment.fields[6] || '';
        const status = segment.fields[7] || '';

        const testResult = {
          testName,
          value,
          unit,
          referenceRange,
          status,
          notes: []
        };

        testResults.push(testResult);
        currentTestIndex = testResults.length - 1;
      } else if (segment.segmentId === 'NTE') {
        const note = segment.fields[2] || '';
        if (note && currentTestIndex >= 0) {
          testResults[currentTestIndex].notes.push(note);
        }
      }
    });

    return {
      segments,
      messageType,
      messageControlId,
      timestamp,
      testResults,
      memberInfo
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to parse HL7 content: ${error.message}`);
    }
    throw error;
  }
};
